/**
 * Advanced device detection utilities for better tablet identification
 */

export interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  hasTouch: boolean;
  hasPen: boolean;
  hasHover: boolean;
  isIPad: boolean;
  screenSize: 'small' | 'medium' | 'large' | 'xlarge';
  orientation: 'portrait' | 'landscape';
}

// Screen size thresholds (in inches, approximated from pixels)
const SCREEN_SIZES = {
  small: { max: 7 },      // Phones
  medium: { min: 7, max: 10 },  // Small tablets
  large: { min: 10, max: 13 },  // Regular tablets
  xlarge: { min: 13 }     // Large tablets (iPad Pro 12.9")
};

/**
 * Calculate approximate screen size in inches
 */
function getScreenSizeInInches(): number {
  // Get screen dimensions
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  
  // Get device pixel ratio
  const dpr = window.devicePixelRatio || 1;
  
  // Calculate physical pixels
  const physicalWidth = screenWidth * dpr;
  const physicalHeight = screenHeight * dpr;
  
  // Assume a typical PPI (pixels per inch) based on device type
  // This is an approximation - actual PPI varies by device
  let ppi = 96; // Default desktop PPI
  
  if (window.matchMedia('(pointer: coarse)').matches) {
    // Mobile/tablet devices typically have higher PPI
    ppi = 264; // Average mobile/tablet PPI
  }
  
  // Calculate diagonal in inches using Pythagorean theorem
  const diagonalPixels = Math.sqrt(physicalWidth ** 2 + physicalHeight ** 2);
  const diagonalInches = diagonalPixels / ppi;
  
  return diagonalInches;
}

/**
 * Detect if device is an iPad (including iPad Pro on iPadOS 13+)
 */
function detectIPad(): boolean {
  return /iPad/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Detect screen size category
 */
function detectScreenSize(): 'small' | 'medium' | 'large' | 'xlarge' {
  const inches = getScreenSizeInInches();
  
  if (inches < SCREEN_SIZES.medium.min) return 'small';
  if (inches < SCREEN_SIZES.large.min) return 'medium';
  if (inches < SCREEN_SIZES.xlarge.min) return 'large';
  return 'xlarge';
}

/**
 * Check for stylus/pen support
 */
function hasPenSupport(): boolean {
  // Check for fine pointer (stylus) without hover capability
  const finePointerNoHover = window.matchMedia('(pointer: fine) and (hover: none)').matches;
  
  // Check for any pointer device
  const anyPointer = window.matchMedia('(any-pointer: fine)').matches;
  
  // Check PointerEvent support with pen
  const pointerEventSupport = 'PointerEvent' in window;
  
  return finePointerNoHover || (anyPointer && pointerEventSupport);
}

/**
 * Main device detection function
 */
export function detectDevice(): DeviceInfo {
  const hasTouch = 'ontouchstart' in window || 
                   navigator.maxTouchPoints > 0 || 
                   (navigator as any).msMaxTouchPoints > 0;
  
  const hasHover = window.matchMedia('(hover: hover)').matches;
  const hasPen = hasPenSupport();
  const isIPad = detectIPad();
  const screenSize = detectScreenSize();
  const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  
  // Determine device type based on multiple factors
  let type: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  
  // Check window width first for more accurate mobile detection
  const windowWidth = window.innerWidth;
  const isMobileWidth = windowWidth < 768; // Standard mobile breakpoint
  
  // Check for iPhone specifically
  const isIPhone = /iPhone/.test(navigator.userAgent) || 
                   (/iPad/.test(navigator.userAgent) && windowWidth < 768);
  
  if (isIPhone) {
    // All iPhones are mobile devices
    type = 'mobile';
  } else if (isIPad) {
    // All iPads are tablets
    type = 'tablet';
  } else if (isMobileWidth && hasTouch) {
    // Width less than 768px with touch = mobile (catches phones)
    type = 'mobile';
  } else if (screenSize === 'small' && hasTouch) {
    // Small screen with touch = mobile
    type = 'mobile';
  } else if ((screenSize === 'medium' || screenSize === 'large') && hasTouch && !hasHover) {
    // Medium/large screen with touch but no hover = tablet
    type = 'tablet';
  } else if (screenSize === 'xlarge' && hasTouch && !hasHover) {
    // Extra large screen with touch but no hover = large tablet
    type = 'tablet';
  } else if (hasTouch && window.matchMedia('(pointer: coarse)').matches) {
    // Has touch with coarse pointer = likely tablet/mobile
    // Use window width as primary check
    type = isMobileWidth ? 'mobile' : 'tablet';
  }
  
  return {
    type,
    hasTouch,
    hasPen,
    hasHover,
    isIPad,
    screenSize,
    orientation
  };
}

/**
 * React hook for device detection with SSR support
 */
export function useDeviceDetection(): DeviceInfo | null {
  if (typeof window === 'undefined') {
    return null;
  }
  
  return detectDevice();
}

/**
 * Check if running as a native mobile app (Capacitor)
 */
export function isNativeMobileApp(): boolean {
  return !!(window as any).Capacitor;
}